import { Button } from '@blofin/blofin-ui';
import SectionContainer from 'components/common/SectionContainer';
import { useAuth } from '@blofin/blofin-component/blofin-provider/auth/hooks/useAuth';
import useLocaleLink from 'hooks/useLocaleLink';
import { useTranslations } from 'next-intl';
import { useCallback, useRef, useState } from 'react';
import type { SelfServiceItem } from 'types/help-center';

const ICONFONT_MAP: Record<string, string> = {
  'phone-line': 'icon-phone-line1',
  'email-line': 'icon-email-line',
  'password-line': 'icon-a-lockTure',
  'google-line': 'icon-googleauthenticator-line',
  'passkey-line': 'icon-passkey-line',
  'deposit-line': 'icon-deposit-line',
  'withdraw-line': 'icon-withdraw-line',
  'anticode-line': 'icon-anticode-line',
  'anticode-line1': 'icon-anticode-line1',
  'account-line': 'icon-account-line',
  'equipment-line': 'icon-equipment-line1',
  'history-line': 'icon-history-line',
};

const MOBILE_ITEMS_PER_PAGE = 9;
const PC_ITEMS_PER_PAGE = 8;
const SWIPE_THRESHOLD = 50;

interface SelfServiceSectionProps {
  items: SelfServiceItem[];
  isMobile?: boolean;
}

export default function SelfServiceSection({ items, isMobile = false }: SelfServiceSectionProps) {
  const t = useTranslations('support');
  const { isLogin } = useAuth();
  const localeLink = useLocaleLink();
  const itemsPerPage = isMobile ? MOBILE_ITEMS_PER_PAGE : PC_ITEMS_PER_PAGE;
  const totalPages = Math.ceil(items.length / itemsPerPage);
  const [currentPage, setCurrentPage] = useState(0);
  const touchStartX = useRef(0);

  const handleItemClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, item: SelfServiceItem) => {
      if (!item.needAuth) return;
      e.preventDefault();
      if (isLogin === null) return;
      if (isLogin) {
        window.location.href = `${localeLink}${item.href}`;
      } else {
        window.location.href = `${localeLink}/login?redirect=${encodeURIComponent(item.href)}`;
      }
    },
    [isLogin, localeLink]
  );

  const goToPrev = useCallback(() => {
    setCurrentPage((prev) => (prev <= 0 ? totalPages - 1 : prev - 1));
  }, [totalPages]);

  const goToNext = useCallback(() => {
    setCurrentPage((prev) => (prev >= totalPages - 1 ? 0 : prev + 1));
  }, [totalPages]);

  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const deltaX = touchStartX.current - e.changedTouches[0].clientX;
      if (deltaX > SWIPE_THRESHOLD) {
        goToNext();
      } else if (deltaX < -SWIPE_THRESHOLD) {
        goToPrev();
      }
    },
    [goToNext, goToPrev]
  );

  return (
    <SectionContainer>
      <h2
        className={`text-[22px] font-bold text-base-label ${isMobile ? 'mb-4' : 'mb-10 text-5xl pc-small:text-left'}`}
      >
        {t('selfServiceTitle')}
      </h2>

      <div className="overflow-hidden" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <div
          className="flex transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${currentPage * 100}%)` }}
        >
          {Array.from({ length: totalPages }).map((_, pageIdx) => (
            <div key={pageIdx} className="w-full shrink-0 px-0.5">
              <div
                className={
                  isMobile
                    ? 'grid grid-cols-3 gap-x-4 gap-y-6'
                    : 'grid grid-cols-2 gap-x-4 gap-y-6 pc-small:grid-cols-4'
                }
              >
                {items.slice(pageIdx * itemsPerPage, (pageIdx + 1) * itemsPerPage).map((item) => (
                  <a
                    key={item.label}
                    href={`${localeLink}${item.href}`}
                    onClick={(e) => handleItemClick(e, item)}
                    className={
                      isMobile
                        ? 'group flex flex-col items-center justify-start gap-2 py-2'
                        : 'group flex flex-col items-center justify-center gap-4 rounded-2xl border border-base-line-primary p-6 backdrop-blur-sm transition-colors dark:border-transparent dark:bg-base-hover-fill-primary dark:hover:bg-base-fill-secondary pc-small:h-[130px]'
                    }
                  >
                    <span
                      className={
                        isMobile
                          ? 'flex size-10 items-center justify-center rounded-2xl bg-base-hover-fill-primary backdrop-blur-sm'
                          : 'flex size-10 items-center justify-center'
                      }
                    >
                      <i
                        className={`iconfont ${ICONFONT_MAP[item.icon] ?? ''} text-base-label transition-colors group-hover:text-base-primary ${isMobile ? 'text-2xl' : 'text-[32px]'}`}
                      />
                    </span>
                    <span
                      className={`text-center text-base-label transition-colors group-hover:text-base-primary ${isMobile ? 'text-xs leading-[18px]' : 'text-base font-medium'}`}
                    >
                      {t(item.label)}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {totalPages >= 1 && (
        <div className={`flex items-center justify-center gap-2 ${isMobile ? 'mt-6' : 'mt-10'}`}>
          <Button
            variant="text"
            size="small"
            onClick={goToPrev}
            className="min-w-5 text-base-label-40 hover:text-base-label"
          >
            <i className="iconfont icon-arrow-left-l-line text-xl" />
          </Button>

          {Array.from({ length: totalPages }).map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => goToPage(idx)}
              className={`h-1.5 w-4 rounded transition-colors ${
                idx === currentPage ? 'bg-base-label-40' : 'bg-base-label-20'
              }`}
              aria-label={t('goToPage', { page: idx + 1 })}
            />
          ))}

          <Button
            variant="text"
            size="small"
            onClick={goToNext}
            className="min-w-5 text-base-label-40 hover:text-base-label"
          >
            <i className="iconfont icon-arrow-right-l-line text-xl" />
          </Button>
        </div>
      )}
    </SectionContainer>
  );
}
